import TextField from '@components/v2/form/TextField/TextField';
import Icon from '@components/v2/ui/Icon/Icon';
import { InputAdornment } from '@mui/material';
import lodash from 'lodash';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useListPick } from '../../components/ListPicker/useListPick';
import AutocompleteMuiAdapter from '../../components/adaptersMui/AutocompleteMuiAdapter/AutocompleteMuiAdapter';
import LangEnum from '../../lang/LangEnum';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../stores/ProductActionsStore/productActions.store';
import { onScrollEndReload } from '../../utils/generics';
import { ActiveOrderTestIds } from '../../utils/testIds/ActiveOrder.testIds';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { useNetworkContext } from '../offline/contexts/network/network.context';
import { orderRepositoryOfflineAdapters } from '../offline/offlineRequestAdapters/order.repository.offlineAdapters';
import { createParamsToUseListPickWhenOffline } from '../offline/utils/createParamsToUseListPickWhenOffline';
import { getStatusToEditByRole } from '../orders/actions/utils/orderSummaryAction.utils';
import { OrderView } from '../orders/ordersView.types';
import useProductDetailsActions from '../products/products-details-v2/hooks/useProductsDetailsActions';
import { useTranslation } from '../translation/TranslationContext';
import OrderBookOptionRenderer from './OrderBookOptionRenderer';
import useOrderBook from './hooks/useOrderBook';
import { ListOrdersV2Output } from './types/orderBook.types';

const OrderAutocomplete = () => {
  const { isOnline } = useNetworkContext();
  const { userRole: role, team } = useApplicationContext();
  const [
    orderAutocompleteFocused,
    handleOrderAutocompleteFocused,
    handleChangeSelectedOrder,
    customer,
    selectedOrder,
    orders,
    isOrderActivable,
  ] = useOrderBookStore(
    useShallow((state) => [
      state.orderAutocompleteFocused,
      state.handleOrderAutocompleteFocused,
      state.handleChangeSelectedOrder,
      state.customer,
      state.selectedOrder,
      state.orders,
      state.isOrderActivable,
    ]),
  );
  const closeProductNotification = useProductActionsStore(
    (state) => state.closeProductNotification,
  );
  const { onEdit } = useOrderBook();
  const { translate } = useTranslation();
  const history = useHistory();
  const { verifyProductDetailsModalOpenedByOrderBook } = useProductDetailsActions();

  const [inputText, setInputText] = useState('');
  const listPickActions = useListPick<ListOrdersV2Output>(
    createParamsToUseListPickWhenOffline({
      isOnline,
      offlineFetcher: orderRepositoryOfflineAdapters.page,
      onlineProps: {
        url: `/orders/v2`,
        order: { createdAt: 'DESC' },
        queryField: 'codeOrCustomer',
        limit: 20,
        defaultQuery: {
          customerId: customer?.id,
          status: getStatusToEditByRole(role),
        },
      },
    }),
  );

  const debouncedInputChange = useRef(
    lodash.debounce((value: string) => {
      listPickActions.onSearch(value);
    }, 500),
  ).current;

  const handleChangeInput = (_: React.ChangeEvent<{}>, value: string) => {
    setInputText(value);
    if (value === '') {
      debouncedInputChange.cancel();
      listPickActions.onSearch('');
      return;
    }
    debouncedInputChange(value);
  };

  const handleBlur = () => {
    handleOrderAutocompleteFocused(false);
  };

  const handleInputFocused = () => {
    handleOrderAutocompleteFocused(true);
    listPickActions.onSearch('');
  };

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (orderAutocompleteFocused) {
      setTimeout(() => {
        setOpen(true);
      }, 50);
    }
    setOpen(orderAutocompleteFocused);
  }, [orderAutocompleteFocused]);

  const handleChange = useCallback(
    (order?: ListOrdersV2Output | null) => {
      if (!order) {
        return;
      }
      const handleRedirectToNewActiveOrder = () => {
        if (!onEdit) {
          return;
        }
        history.push(`/orders/${order?.id}/${OrderView.EDIT}`);
      };
      handleOrderAutocompleteFocused(false);
      handleChangeSelectedOrder({
        order,
        teamId: team?.id,
        translate,
        redirectOrder: handleRedirectToNewActiveOrder,
      });
      closeProductNotification();
      verifyProductDetailsModalOpenedByOrderBook(order.id);
    },
    [
      closeProductNotification,
      handleChangeSelectedOrder,
      handleOrderAutocompleteFocused,
      history,
      onEdit,
      team?.id,
      translate,
      verifyProductDetailsModalOpenedByOrderBook,
    ],
  );

  return (
    <AutocompleteMuiAdapter<ListOrdersV2Output>
      open={open}
      key={selectedOrder?.id}
      onChange={(_, order) => {
        handleChange(order);
      }}
      clearOnBlur={false}
      inputValue={inputText}
      loading={listPickActions.loading}
      onInputChange={handleChangeInput}
      options={listPickActions.leftItems.filter(
        (order) =>
          !orders.some((orderBookOrder) => order.id === orderBookOrder.id) &&
          isOrderActivable({
            orderStatus: order.status,
            synchronized: order.synchronized,
            role,
            team,
          }) &&
          order.id !== selectedOrder?.id,
      )}
      noOptionsText={translate(LangEnum.GLOBAL_NO_OPTIONS)}
      loadingText={translate(LangEnum.GLOBAL_SEARCHING)}
      slotProps={{
        listbox: {
          style: {
            maxHeight: 250,
          },
          onScroll: (event: React.SyntheticEvent) => onScrollEndReload(event, listPickActions),
        },
      }}
      test-id={ActiveOrderTestIds.ORDER_BOOK_ORDERS_AUTOCOMPLETE}
      filterOptions={(options) => options}
      getOptionLabel={(option: ListOrdersV2Output) => option.code}
      disableClearable={false}
      forcePopupIcon={false}
      renderOption={(option: ListOrdersV2Output) => <OrderBookOptionRenderer order={option} />}
      renderInput={(params) => (
        <TextField
          id={params.id}
          size="medium"
          disabled={params.disabled}
          placeholder={translate(LangEnum.GLOBAL_EXISTING_ORDERS)}
          onFocus={handleInputFocused}
          onBlur={handleBlur}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={Search} size="md" />
                </InputAdornment>
              ),
            },
            htmlInput: params.inputProps,
          }}
        />
      )}
    />
  );
};

export default OrderAutocomplete;
